/**
 * The admin-managed AI model list (2026-09-04), as every dropdown in the app
 * reads it: a module SINGLETON, loaded once from `GET /api/ai/model-list` and
 * refreshed whenever a board opens or an admin edits the list.
 *
 * ONE list, one look, one place to change it. Before this, each picker made
 * its own arrangements — the thumbnail panel ranked models from a hand-written
 * table in client code (a redeploy to add one), while the brainstorm and
 * sections pickers dumped the provider's whole ~400-entry catalog — so no two
 * dropdowns in Kola offered the same models or described them the same way.
 *
 * Why a singleton and not per-board state: the list is WORKSPACE-wide, and the
 * panels that offer it (script brainstorm, sections, thumbnails) sit deep in
 * trees with no prop path from the app shell — the same situation the `/` kinds
 * registry and the F-27 translate store are in.
 *
 * NO NETWORK IMPORT HERE, on purpose: the fetch lives in ./load.ts. The
 * pickers live inside board panels, and the server's e2e harnesses typecheck
 * the client's grid module for one type — which pulls whatever those
 * components import into a NODE program, where `core/api.ts`'s
 * `import.meta.env` (a Vite-only global) does not compile. Keeping the state
 * pure and the loader separate means a picker never drags fetch into that graph.
 */
import type { AiFunctionKey, AiModelEntry, AiModelKind } from "@kola/shared";
import { useMemo } from "react";
import { useStore } from "zustand";
import { createStore, type StoreApi } from "zustand/vanilla";

export interface AiModelsState {
  /** The list, in the order the admin page shows it (oldest entry first). */
  models: readonly AiModelEntry[];
  /** What a model-less run resolves to per function, for THIS user. */
  defaults: Partial<Record<AiFunctionKey, string>>;
  /** True once the first load (or a later refresh) has answered. */
  loaded: boolean;
  setList(models: readonly AiModelEntry[], defaults: Partial<Record<AiFunctionKey, string>>): void;
}

export type AiModelsStore = StoreApi<AiModelsState>;

export const aiModels: AiModelsStore = createStore<AiModelsState>((set) => ({
  models: [],
  defaults: {},
  loaded: false,
  setList: (models, defaults) => set({ models: [...models], defaults: { ...defaults }, loaded: true }),
}));

/** The whole list, live — for the admin page. */
export function useAiModels(): readonly AiModelEntry[] {
  return useStore(aiModels, (s) => s.models);
}

/** True once a load has answered — a picker renders "Loading…" before it. */
export function useAiModelsLoaded(): boolean {
  return useStore(aiModels, (s) => s.loaded);
}

/**
 * The model a run with no explicit pick will use, for one function — what the
 * "Default" option names. `undefined` before the first load answers.
 */
export function useAiModelDefault(functionKey: AiFunctionKey): string | undefined {
  return useStore(aiModels, (s) => s.defaults[functionKey]);
}

/**
 * The entries a picker for `kind` may offer. The split is the one distinction
 * a picker must not get wrong: an image model on a text function returns
 * nothing usable, and a text model on the thumbnail render fails every frame.
 *
 * The filter runs in a memo rather than inside the store selector: a selector
 * that builds a fresh array every call has no stable snapshot, which
 * `useSyncExternalStore` (zustand's `useStore`) reads as "changed again" and
 * spins.
 */
export function useAiModelsOfKind(kind: AiModelKind): readonly AiModelEntry[] {
  const models = useAiModels();
  return useMemo(() => selectByKind(models, kind), [models, kind]);
}

/** Pure — exported for the picker's tests. */
export function selectByKind(
  models: readonly AiModelEntry[],
  kind: AiModelKind,
): readonly AiModelEntry[] {
  return models.filter((model) => model.kind === kind);
}
